import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Star } from "lucide-react";

interface RatingDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (rating: number, comment: string) => void;
  isSubmitting?: boolean;
}

const CustomRating: React.FC<{
  value: number;
  onChange: (value: number) => void;
  max?: number;
  size?: "small" | "medium" | "large";
  disabled?: boolean;
}> = ({ value, onChange, max = 5, size = "medium", disabled }) => {
  const [hoveredStar, setHoveredStar] = React.useState<number>(0);
  const stars = Array.from({ length: max }, (_, i) => i + 1);
  const starSize = size === "large" ? "w-8 h-8" : size === "medium" ? "w-6 h-6" : "size-5";

  return (
    <div className="flex gap-1" onMouseLeave={() => setHoveredStar(0)}>
      {stars.map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHoveredStar(star)}
          disabled={disabled}
          className={`transition-colors`}
        >
          <Star
            className={`${starSize} ${(hoveredStar >= star || value >= star) ? "text-warning-200 fill-current" : ""
              }`}
          />
        </button>
      ))}
    </div>
  );
};

const RatingDialog: React.FC<RatingDialogProps> = ({
  isOpen,
  onOpenChange,
  onSubmit,
  isSubmitting = false,
}) => {
  const [rating, setRating] = React.useState<number>(0);
  const [comment, setComment] = React.useState<string>("");

  React.useEffect(() => {
    if (!isOpen) {
      setRating(0);
      setComment("");
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (rating > 0) {
      onSubmit(rating, comment);
    }
  };

  const isSubmitDisabled = rating === 0 || isSubmitting;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md gap-5">
        <DialogHeader>
          <DialogTitle className="text-lg font-medium text-left">Rate Response</DialogTitle>
        </DialogHeader>
        <div className="grid gap-2">
          <Label className="text-neutral-400 text-sm font-medium">
            Rating<span className="text-danger-200">*</span>
          </Label>
          <CustomRating value={rating} onChange={setRating} disabled={isSubmitting} />
        </div>
        <div className="grid gap-2">
          <Label className="text-neutral-400 text-sm font-medium" htmlFor="comment">
            Additional comments (optional)
          </Label>
          <Textarea
            id="comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share your thoughts about this response..."
            className="min-h-[100px] resize-y placeholder:text-neutral-500"
            disabled={isSubmitting}
          />
        </div>
        <DialogFooter className="flex-row justify-end">
          <DialogClose asChild>
            <Button variant="outline" disabled={isSubmitting}>
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={handleSubmit} disabled={isSubmitDisabled}>
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RatingDialog;
